<script setup>
import { XMarkIcon } from "@heroicons/vue/24/solid";
import LoadingSpinner from "@js/components/LoadingSpinner.vue";
import DataTableLayout from "@js/components/tables/DataTableLayout.vue";
import { useDataTable } from "@js/utils/dataTable";
import { formatPrice, formatTime } from "@js/utils/formatter";
import { useRoute } from "@js/utils/route";
import { createColumnHelper } from "@tanstack/vue-table";
import { h } from "vue";
import { useI18n } from "vue-i18n";
import LinkCellV2 from "@js/components/tables/cells/LinkCellV2.vue";

const { t } = useI18n();

const { userSessionKey, type } = defineProps({
  userSessionKey: {
    type: String,
    required: false,
    default: null,
  },
  type: {
    type: String,
    required: false,
    default: null,
  },
});

const { route } = useRoute();

const columnHelper = createColumnHelper();

const {
  loading,
  table,
  count,
  globalFilter,
  filters,
  availableFilters,
  setGlobalFilter,
  setFilters,
} = useDataTable({
  columns: [
    columnHelper.accessor(() => null, {
      id: "name",
      header: () => t("label.name"),
      cell: (props) =>
        h(LinkCellV2, {
          text: props.row.original.name,
          href:
            route("front.projectManagement.customer.updateCustomer", {
              id: props.row.original.id,
            }),
          target: "_self",
          inertia: true,
        }),
    }),
    columnHelper.accessor(() => null, {
      id: "seo",
      header: () => t("label.seo"),
      cell: (props) =>
        h(
          "div",
          {
            class:
              "flex flex-col justify-center h-full whitespace-nowrap p-4 font-bold justify-start gap-1",
          },
          [
            h("div", { innerHTML: "&nbsp;", class: "text-xs" }),
            props.row.original.seoPackage
              ? h("div", {
                  innerText: formatPrice(props.row.original.seoPackage.price),
                  class: "text-lg",
                })
              : h(XMarkIcon, {
                  class: "size-5 text-red-500",
                }),
          ],
        ),
      enableSorting: false,
    }),
    columnHelper.accessor(() => null, {
      id: "sea",
      header: () => t("label.lastMonthSea"),
      cell: (props) =>
        h(
          "div",
          {
            class:
              "flex flex-col justify-center h-full whitespace-nowrap p-4 font-bold justify-start gap-1",
          },
          props.row.original.seaPackage
            ? props.row.original.suggestedSeaPackage &&
              props.row.original.suggestedSeaPackage.id !==
                props.row.original.seaPackage.id
              ? [
                  h("div", {
                    innerText: formatPrice(props.row.original.seaPackage.price),
                    class: "text-xs line-through",
                  }),
                  h("div", {
                    innerText: formatPrice(
                      props.row.original.suggestedSeaPackage.price,
                    ),
                    class: "text-lg",
                  }),
                ]
              : [
                  h("div", { innerHTML: "&nbsp;", class: "text-xs" }),
                  h("div", {
                    innerText: formatPrice(props.row.original.seaPackage.price),
                    class: "text-lg",
                  }),
                ]
            : [
                h("div", { innerHTML: "&nbsp;", class: "text-xs" }),
                h(XMarkIcon, {
                  class: "size-5 text-red-500",
                }),
              ],
        ),
      enableSorting: false,
    }),
    columnHelper.accessor(() => null, {
      id: "smo",
      header: () => t("label.smo"),
      cell: (props) =>
        h(
          "div",
          {
            class:
              "flex flex-col justify-center h-full whitespace-nowrap p-4 font-bold justify-start gap-1",
          },
          [
            h("div", { innerHTML: "&nbsp;", class: "text-xs" }),
            props.row.original.smoPackage
              ? h("div", {
                  innerText: formatPrice(props.row.original.smoPackage.price),
                  class: "text-lg",
                })
              : h(XMarkIcon, {
                  class: "size-5 text-red-500",
                }),
          ],
        ),
      enableSorting: false,
    }),
    columnHelper.accessor(() => null, {
      id: "sma",
      header: () => t("label.lastMonthSma"),
      cell: (props) =>
        h(
          "div",
          {
            class:
              "flex flex-col justify-center h-full whitespace-nowrap p-4 font-bold justify-start gap-1",
          },
          props.row.original.smaPackage
            ? props.row.original.suggestedSmaPackage &&
              props.row.original.suggestedSmaPackage.id !==
                props.row.original.smaPackage.id
              ? [
                  h("div", {
                    innerText: formatPrice(props.row.original.smaPackage.price),
                    class: "text-xs line-through",
                  }),
                  h("div", {
                    innerText: formatPrice(
                      props.row.original.suggestedSmaPackage.price,
                    ),
                    class: "text-lg",
                  }),
                ]
              : [
                  h("div", { innerHTML: "&nbsp;", class: "text-xs" }),
                  h("div", {
                    innerText: formatPrice(props.row.original.smaPackage.price),
                    class: "text-lg",
                  }),
                ]
            : [
                h("div", { innerHTML: "&nbsp;", class: "text-xs" }),
                h(XMarkIcon, {
                  class: "size-5 text-red-500",
                }),
              ],
        ),
      enableSorting: false,
    }),
    columnHelper.accessor(() => null, {
      id: "sem",
      header: () => t("label.sem"),
      cell: (props) =>
        h(
          "div",
          {
            class:
              "flex flex-col justify-center h-full whitespace-nowrap p-4 font-bold justify-start gap-1",
          },
          [
            h("div", { innerHTML: "&nbsp;", class: "text-xs" }),
            props.row.original.semPackage
              ? h("div", {
                  innerText: formatPrice(props.row.original.semPackage.price),
                  class: "text-lg",
                })
              : h(XMarkIcon, {
                  class: "size-5 text-red-500",
                }),
          ],
        ),
      enableSorting: false,
    }),
    columnHelper.accessor(() => null, {
      id: "dev",
      header: () => t("label.lastMonthDev"),
      cell: (props) =>
        h(
          "div",
          {
            class:
              "flex flex-col justify-center h-full whitespace-nowrap p-4 font-bold justify-start gap-1",
          },
          props.row.original.dev
            ? [
                h("div", {
                  innerText: formatTime(
                    props.row.original.dev.hours,
                    props.row.original.dev.minutes,
                  ),
                  class: "text-xs",
                }),
                h("div", {
                  innerText: formatPrice(props.row.original.dev.price),
                  class: "text-lg",
                }),
              ]
            : [
                h("div", { innerHTML: "&nbsp;", class: "text-xs" }),
                h(XMarkIcon, {
                  class: "size-5 text-red-500",
                }),
              ],
        ),
      enableSorting: false,
    }),
    columnHelper.accessor(() => null, {
      id: "hour",
      header: () => t("label.lastMonthHour"),
      cell: (props) =>
        h(
          "div",
          {
            class:
              "flex flex-col justify-center h-full whitespace-nowrap p-4 font-bold justify-start gap-1",
          },
          [
            props.row.original.hour
              ? [
                  h("div", {
                    innerText: formatTime(
                      props.row.original.hour.hours,
                      props.row.original.hour.minutes,
                    ),
                    class: "text-xs",
                  }),
                  h("div", {
                    innerText: formatPrice(props.row.original.hour.price),
                    class: "text-lg",
                  }),
                ]
              : [
                  h("div", { innerHTML: "&nbsp;", class: "text-xs" }),
                  h(XMarkIcon, {
                    class: "size-5 text-red-500",
                  }),
                ],
          ],
        ),
      enableSorting: false,
    }),
    columnHelper.accessor(() => null, {
      id: "shoppingfeed",
      header: () => t("abbreviation.shoppingfeed"),
      cell: (props) =>
        h(
          "div",
          {
            class:
              "flex flex-col justify-center h-full whitespace-nowrap p-4 font-bold justify-start gap-1",
          },
          [
            h("div", { innerHTML: "&nbsp;", class: "text-xs" }),
            props.row.original.shoppingfeedPackage
              ? h("div", {
                  innerText: formatPrice(
                    props.row.original.shoppingfeedPackage.price,
                  ),
                  class: "text-lg",
                })
              : h(XMarkIcon, {
                  class: "size-5 text-red-500",
                }),
          ],
        ),
      enableSorting: false,
    }),
    columnHelper.accessor(() => null, {
      id: "cloudflare",
      header: () => t("abbreviation.cloudflare"),
      cell: (props) =>
        h(
          "div",
          {
            class:
              "flex flex-col justify-center h-full whitespace-nowrap p-4 font-bold justify-start gap-1",
          },
          [
            h("div", { innerHTML: "&nbsp;", class: "text-xs" }),
            props.row.original.cloudflarePackage
              ? h("div", {
                  innerText: formatPrice(
                    props.row.original.cloudflarePackage.price,
                  ),
                  class: "text-lg",
                })
              : h(XMarkIcon, {
                  class: "size-5 text-red-500",
                }),
          ],
        ),
      enableSorting: false,
    }),
    columnHelper.accessor(() => null, {
      id: "special",
      header: () => t("label.special"),
      cell: (props) =>
        h(
          "div",
          {
            class:
              "flex flex-col justify-center h-full p-4 font-bold justify-start gap-1",
          },
          props.row.original.specialPackage
            ? [
                h("div", {
                  innerText: props.row.original.specialPackage.name,
                  class: "text-xs",
                }),
                h("div", { innerHTML: "&nbsp;", class: "text-lg" }),
              ]
            : [
                h("div", { innerHTML: "&nbsp;", class: "text-xs" }),
                h(XMarkIcon, {
                  class: "size-5 text-red-500",
                }),
              ],
        ),
      enableSorting: false,
    }),
    columnHelper.accessor(() => null, {
      id: "hosting",
      header: () => t("label.hosting"),
      cell: (props) =>
        h(
          "div",
          {
            class:
              "flex flex-col justify-center h-full whitespace-nowrap p-4 font-bold justify-start gap-1",
          },
          [
            h("div", { innerHTML: "&nbsp;", class: "text-xs" }),
            props.row.original.hostingPackage
              ? h("div", {
                innerText: formatPrice(
                  props.row.original.hostingPackage.price,
                ),
                class: "text-lg",
              })
              : h(XMarkIcon, {
                class: "size-5 text-red-500",
              }),
          ],
        ),
      enableSorting: false,
    }),
    columnHelper.accessor(() => null, {
      id: "last2MonthsTurnoverExcludingTax",
      header: () => t("label.last2MonthsTurnoverExcludingTax"),
      cell: (props) =>
        h(
          "div",
          {
            class:
              "flex flex-col justify-center h-full whitespace-nowrap p-4 font-bold justify-start gap-1",
          },
          props.row.original.last2MonthsTurnoverExcludingTax
            ? [
                h("div", {
                  innerText: props.row.original.last2MonthsDate,
                  class: "text-xs",
                }),
                h("div", {
                  innerHTML: formatPrice(
                    props.row.original.last2MonthsTurnoverExcludingTax,
                    false,
                  ),
                  class: "text-lg",
                }),
              ]
            : [
                h("div", {
                  innerHTML: "&nbsp;",
                  class: "text-xs",
                }),
                h(XMarkIcon, {
                  class: "size-5 text-red-500",
                }),
              ],
        ),
      enableSorting: false,
    }),
    columnHelper.accessor(() => null, {
      id: "last2MonthsTurnoverExcludingTaxWithoutShipping",
      header: () => t("label.last2MonthsTurnoverExcludingTaxWithoutShipping"),
      cell: (props) =>
        h(
          "div",
          {
            class:
              "flex flex-col justify-center h-full whitespace-nowrap p-4 font-bold justify-start gap-1",
          },
          props.row.original.last2MonthsTurnoverExcludingTaxWithoutShipping
            ? [
                h("div", {
                  innerText: props.row.original.last2MonthsDate,
                  class: "text-xs",
                }),
                h("div", {
                  innerHTML: formatPrice(
                    props.row.original
                      .last2MonthsTurnoverExcludingTaxWithoutShipping,
                    false,
                  ),
                  class: "text-lg",
                }),
              ]
            : [
                h("div", {
                  innerHTML: "&nbsp;",
                  class: "text-xs",
                }),
                h(XMarkIcon, {
                  class: "size-5 text-red-500",
                }),
              ],
        ),
      enableSorting: false,
    }),
  ],
  url: route("front.projectManagement.apikom.postInvoicing"),
  data: {
    type,
  },
  userSessionKey,
});
</script>

<template>
  <LoadingSpinner v-if="loading" />

  <div v-else>
    <DataTableLayout
      :table="table"
      :count="count"
      :global-filter="globalFilter"
      :filters="filters"
      :available-filters="availableFilters"
      :set-filters="setFilters"
      :set-global-filter="setGlobalFilter"
    />
  </div>
</template>
